/**
 * Модальный диалог по центру — вопрос с выбором, подтверждение.
 *
 * Как `Drawer`: фокус уходит в диалог и не выходит из него по Tab, Esc и
 * щелчок по подложке закрывают, остальное приложение под `inert`, после
 * закрытия фокус возвращается туда, откуда открыли. Подложка — по видимой
 * области (visual viewport), на телефоне диалог во всю ширину.
 */
import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { focusables, setAppInert } from '@shared/lib/appInert';
import { fontSize, lineHeight } from '@shared/theme/typeScale';
import { useVisualViewportBox } from './Drawer';
import { IconX } from './icons';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  left: var(--vv-left, 0px);
  top: var(--vv-top, 0px);
  width: var(--vv-width, 100%);
  height: var(--vv-height, 100%);
  z-index: ${({ theme }) => theme.z.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.background}C7;
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  animation: ${fadeIn} ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
`;

const WIDTH = { sm: '440px', md: '560px' } as const;

const Panel = styled.div<{ $size: keyof typeof WIDTH }>`
  width: min(${({ $size }) => WIDTH[$size]}, 100%);
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  animation: ${rise} ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.lg};
  }
`;

const Head = styled.header`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(5)} ${theme.spacing(2)}`};
`;

const Title = styled.h2`
  flex: 1;
  margin: 0;
  font-size: ${fontSize.lg};
  line-height: ${lineHeight.tight};
  font-weight: 600;
  text-wrap: balance;
`;

const Close = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: -6px -6px 0 0;
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(5)} ${theme.spacing(4)}`};
  font-size: ${fontSize.md};
  line-height: ${lineHeight.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

export const DialogFooter = styled.footer`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(5)} ${theme.spacing(5)}`};
`;

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  size?: keyof typeof WIDTH;
  /** `alertdialog` — вопрос, на который надо ответить (подтверждение). */
  role?: 'dialog' | 'alertdialog';
  /** Что получит фокус при открытии; по умолчанию — первая кнопка ответа. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
  footer?: ReactNode;
}

export function Dialog({ open, ...props }: DialogProps) {
  if (!open) return null;
  return createPortal(<DialogPanel {...props} />, document.body);
}

function DialogPanel({
  onClose,
  title,
  size = 'sm',
  role = 'dialog',
  initialFocusRef,
  children,
  footer,
}: Omit<DialogProps, 'open'>) {
  const titleId = useId();
  const bodyId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useVisualViewportBox(backdropRef);

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  // При открытии: запомнить, откуда пришли, и увести фокус в диалог —
  // на заданный элемент или на последнюю кнопку ответа (основное действие).
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setAppInert(true);
    const panel = panelRef.current;
    const target =
      initialFocusRef?.current ??
      panel?.querySelector<HTMLElement>('footer button:last-of-type') ??
      panel;
    target?.focus({ preventScroll: true });
    return () => {
      setAppInert(false);
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeRef.current();
      return;
    }
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const items = focusables(panel);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <Backdrop
      ref={backdropRef}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeRef.current();
      }}
    >
      <Panel
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        $size={size}
        onKeyDown={onKeyDown}
      >
        <Head>
          <Title id={titleId}>{title}</Title>
          <Close type="button" onClick={() => closeRef.current()} aria-label="Закрыть" title="Закрыть (Esc)">
            <IconX size={18} />
          </Close>
        </Head>
        <Body id={bodyId}>{children}</Body>
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </Panel>
    </Backdrop>
  );
}
